import { z } from 'zod';
import { moneyAmountSchema, moneySchema } from './catalog.js';

/**
 * Arabat's own hardware in the platform console (ADR-0156): device units with their status
 * history, demo kits and their loans, devices sold to merchants and repairs. Amounts are fils on
 * the server and decimal strings on the wire; dates are calendar days (YYYY-MM-DD).
 */
export const HARDWARE_UNIT_STATUSES = [
  'in_stock',
  'demo_kit',
  'on_trial',
  'sold',
  'under_repair',
  'retired',
] as const;
export type HardwareUnitStatus = (typeof HARDWARE_UNIT_STATUSES)[number];
/** Statuses an operator sets directly; `sold` and `under_repair` come from sales and repairs. */
export const HARDWARE_MANUAL_STATUSES = ['in_stock', 'demo_kit', 'on_trial', 'retired'] as const;

const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v == null || v === '' ? null : v));
const optionalDate = z
  .union([z.iso.date(), z.literal('')])
  .nullish()
  .transform((v) => (v ? v : null));
const version = z.number().int().nonnegative();

const merchantRefSchema = z.object({
  id: z.string(),
  nameEn: z.string().nullable(),
  nameAr: z.string().nullable(),
});
export type HardwareMerchantRef = z.infer<typeof merchantRefSchema>;

// ---------------------------------------------------------------- units
export const hardwareUnitSchema = z.object({
  id: z.string(),
  offerItemId: z.string().nullable(),
  model: z.string(),
  serialNumber: z.string(),
  purchasedOn: z.string().nullable(),
  purchaseCost: moneySchema,
  status: z.enum(HARDWARE_UNIT_STATUSES),
  holderMerchant: merchantRefSchema.nullable(),
  kitId: z.string().nullable(),
  kitName: z.string().nullable(),
  trialRequestId: z.string().nullable(),
  warrantyEndsOn: z.string().nullable(),
  notes: z.string().nullable(),
  version: z.number().int(),
  createdAt: z.string(),
});
export type HardwareUnit = z.infer<typeof hardwareUnitSchema>;

export const hardwareUnitListSchema = z.object({
  units: z.array(hardwareUnitSchema),
  counts: z.record(z.enum(HARDWARE_UNIT_STATUSES), z.number().int()),
});
export type HardwareUnitList = z.infer<typeof hardwareUnitListSchema>;

export const hardwareUnitQuerySchema = z.object({
  status: z.enum(HARDWARE_UNIT_STATUSES).optional(),
  trialRequestId: z.uuid().optional(),
});

export const createHardwareUnitSchema = z.object({
  offerItemId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  model: z.string().trim().min(1).max(120),
  serialNumber: z.string().trim().min(1).max(80),
  purchasedOn: optionalDate,
  purchaseCost: moneyAmountSchema,
  warrantyEndsOn: optionalDate,
  notes: optionalText(1000),
});
export type CreateHardwareUnit = z.input<typeof createHardwareUnitSchema>;

export const updateHardwareUnitSchema = z.object({
  warrantyEndsOn: optionalDate,
  notes: optionalText(1000),
  version,
});
export type UpdateHardwareUnit = z.input<typeof updateHardwareUnitSchema>;

export const changeHardwareStatusSchema = z.object({
  status: z.enum(HARDWARE_MANUAL_STATUSES),
  /** Required for `demo_kit`. */
  kitId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  /** For `on_trial`: the merchant, or an approved / delivered device-trial request. */
  merchantId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  trialRequestId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
  note: optionalText(500),
  version,
});
export type ChangeHardwareStatus = z.input<typeof changeHardwareStatusSchema>;

export const hardwareUnitEventSchema = z.object({
  id: z.string(),
  fromStatus: z.enum(HARDWARE_UNIT_STATUSES).nullable(),
  toStatus: z.enum(HARDWARE_UNIT_STATUSES),
  holderMerchant: merchantRefSchema.nullable(),
  kitName: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.string(),
});
export type HardwareUnitEvent = z.infer<typeof hardwareUnitEventSchema>;
export const hardwareUnitEventListSchema = z.object({ events: z.array(hardwareUnitEventSchema) });

// ---------------------------------------------------------------- kits
const kitLoanSchema = z.object({
  id: z.string(),
  holderName: z.string(),
  outOn: z.string(),
  expectedReturnOn: z.string().nullable(),
  returnedOn: z.string().nullable(),
  note: z.string().nullable(),
  overdue: z.boolean(),
});
export type HardwareKitLoan = z.infer<typeof kitLoanSchema>;

export const hardwareKitSchema = z.object({
  id: z.string(),
  name: z.string(),
  notes: z.string().nullable(),
  unitCount: z.number().int(),
  currentLoan: kitLoanSchema.nullable(),
  version: z.number().int(),
});
export type HardwareKit = z.infer<typeof hardwareKitSchema>;
export const hardwareKitListSchema = z.object({
  kits: z.array(hardwareKitSchema),
  counts: z.object({ total: z.number().int(), out: z.number().int(), overdue: z.number().int() }),
});
export type HardwareKitList = z.infer<typeof hardwareKitListSchema>;

export const createHardwareKitSchema = z.object({
  name: z.string().trim().min(1).max(80),
  notes: optionalText(1000),
});
export type CreateHardwareKit = z.input<typeof createHardwareKitSchema>;

export const lendHardwareKitSchema = z
  .object({
    holderName: z.string().trim().min(1).max(120),
    outOn: z.iso.date(),
    expectedReturnOn: optionalDate,
    note: optionalText(500),
  })
  .refine((v) => !v.expectedReturnOn || v.expectedReturnOn >= v.outOn, {
    path: ['expectedReturnOn'],
    message: 'The return date cannot be before the out date.',
  });
export type LendHardwareKit = z.input<typeof lendHardwareKitSchema>;

export const returnHardwareKitSchema = z.object({ returnedOn: z.iso.date() });

// ---------------------------------------------------------------- sales
export const hardwareSaleSchema = z.object({
  id: z.string(),
  reference: z.string(),
  unitId: z.string(),
  model: z.string(),
  serialNumber: z.string(),
  buyer: merchantRefSchema,
  price: moneySchema,
  soldOn: z.string(),
  paid: z.boolean(),
  paidOn: z.string().nullable(),
  paymentReference: z.string().nullable(),
  delivered: z.boolean(),
  deliveredOn: z.string().nullable(),
  note: z.string().nullable(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
  version: z.number().int(),
});
export type HardwareSale = z.infer<typeof hardwareSaleSchema>;
export const hardwareSaleListSchema = z.object({
  sales: z.array(hardwareSaleSchema),
  counts: z.object({
    live: z.number().int(),
    unpaid: z.number().int(),
    undelivered: z.number().int(),
    voided: z.number().int(),
  }),
});
export type HardwareSaleList = z.infer<typeof hardwareSaleListSchema>;
export const HARDWARE_SALE_FILTERS = ['all', 'unpaid', 'undelivered'] as const;
export const hardwareSaleQuerySchema = z.object({
  filter: z.enum(HARDWARE_SALE_FILTERS).default('all'),
});

export const createHardwareSaleSchema = z.object({
  unitId: z.uuid(),
  merchantId: z.uuid(),
  price: moneyAmountSchema,
  soldOn: z.iso.date(),
  paid: z.boolean(),
  paymentReference: optionalText(120),
  delivered: z.boolean(),
  note: optionalText(500),
});
export type CreateHardwareSale = z.input<typeof createHardwareSaleSchema>;

/** Marks a sale paid and / or delivered; neither can be undone (void the sale instead). */
export const updateHardwareSaleSchema = z.object({
  paid: z.boolean(),
  paymentReference: optionalText(120),
  delivered: z.boolean(),
  version,
});
export type UpdateHardwareSale = z.input<typeof updateHardwareSaleSchema>;

export const voidHardwareSaleSchema = z.object({
  reason: z.string().trim().min(3).max(300),
  version,
});

// ---------------------------------------------------------------- repairs
export const hardwareRepairSchema = z.object({
  id: z.string(),
  unitId: z.string(),
  model: z.string(),
  serialNumber: z.string(),
  reportedBy: merchantRefSchema.nullable(),
  problem: z.string(),
  reportedOn: z.string(),
  previousStatus: z.enum(HARDWARE_UNIT_STATUSES),
  cost: moneySchema.nullable(),
  resolution: z.string().nullable(),
  resolvedOn: z.string().nullable(),
  version: z.number().int(),
});
export type HardwareRepair = z.infer<typeof hardwareRepairSchema>;
export const hardwareRepairListSchema = z.object({
  repairs: z.array(hardwareRepairSchema),
  counts: z.object({ open: z.number().int(), resolved: z.number().int() }),
});
export type HardwareRepairList = z.infer<typeof hardwareRepairListSchema>;
export const hardwareRepairQuerySchema = z.object({
  state: z.enum(['open', 'resolved', 'all']).default('all'),
});

export const openHardwareRepairSchema = z.object({
  unitId: z.uuid(),
  problem: z.string().trim().min(1).max(1000),
  reportedOn: z.iso.date(),
  /** The merchant who reported it; defaults to the unit's holder. */
  reportedByMerchantId: z
    .uuid()
    .nullish()
    .transform((v) => v ?? null),
});
export type OpenHardwareRepair = z.input<typeof openHardwareRepairSchema>;

export const resolveHardwareRepairSchema = z.object({
  cost: moneyAmountSchema,
  resolution: z.string().trim().min(1).max(1000),
  resolvedOn: z.iso.date(),
  version,
});
export type ResolveHardwareRepair = z.input<typeof resolveHardwareRepairSchema>;
